import type { FormEvent, KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  Add20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarUploader } from "../../components/AvatarUploader";
import { EntityDocumentsSection } from "../../components/EntityDocumentsSection";
import {
  ApiError,
  getClientById,
  getWorkTypes,
  updateClient,
  uploadClientLogo,
  type Client,
  type ClientPayload,
  type WorkType,
} from "../../clients/clientsApi";
import { APP_LOCALE } from "../../config/locale";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar clientes.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o cliente.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar clientes.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o cliente.";
};

const parseWorkTypesError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar tipos de obra.";
    }
  }

  return "Não foi possível carregar os tipos de obra.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const CONTRACT_DOCUMENT_CATEGORIES = [
  "Empreitada",
  "Subempreitada",
  "Fornecimento",
  "Arrendamento",
  "Prestação de serviços",
  "Outro",
];

export function EditarClienteView() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [clientSnapshot, setClientSnapshot] = useState<Client | null>(
    null,
  );
  const [condicoesPagamento, setCondicoesPagamento] =
    useState("30 dias");
  const [tiposObrasDisponiveis, setTiposObrasDisponiveis] =
    useState<WorkType[]>([]);
  const [tiposObrasSelecionados, setTiposObrasSelecionados] =
    useState<number[]>([]);
  const [tiposObrasErro, setTiposObrasErro] = useState<
    string | null
  >(null);
  const [tags, setTags] = useState<string[]>([]);
  const [novaTag, setNovaTag] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [nomeCliente, setNomeCliente] = useState("");
  const [nif, setNif] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [website, setWebsite] = useState("");
  const [morada, setMorada] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [localidade, setLocalidade] = useState("");
  const [pais, setPais] = useState("");
  const [contactoNome, setContactoNome] = useState("");
  const [funcao, setFuncao] = useState("");
  const [contactoEmail, setContactoEmail] = useState("");
  const [contactoTelefone, setContactoTelefone] = useState("");
  const [notasInternas, setNotasInternas] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<
    string | null
  >(null);

  useEffect(() => {
    let isMounted = true;

    const loadClient = async () => {
      if (!clientId) {
        setLoadError("Cliente inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(clientId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Cliente inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getClientById(parsedId);
        if (!isMounted) return;

        setClientSnapshot(response);
        setNomeCliente(response.name ?? "");
        setNif(response.taxId ?? "");
        setEmail(response.email ?? "");
        setTelefone(response.phoneNumber ?? "");
        setWebsite(response.website ?? "");
        setMorada(response.address ?? "");
        setCodigoPostal(response.postalCode ?? "");
        setLocalidade(response.city ?? "");
        setPais(response.country ?? "");
        setContactoNome(response.primaryContactName ?? "");
        setFuncao(response.primaryContactRole ?? "");
        setContactoEmail(response.primaryContactEmail ?? "");
        setContactoTelefone(response.primaryContactPhone ?? "");
        setNotasInternas(response.notes ?? "");
        setCondicoesPagamento(response.paymentTerms ?? "30 dias");
        setTiposObrasSelecionados(
          response.workTypes?.map((workType) => workType.id) ??
            [],
        );
        setTags(response.tags ?? []);
        setLogoUrl(response.logoUrl ?? null);
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadClient();

    return () => {
      isMounted = false;
    };
  }, [clientId]);

  useEffect(() => {
    let isMounted = true;

    const loadWorkTypes = async () => {
      setTiposObrasErro(null);
      try {
        const response = await getWorkTypes();
        if (!isMounted) return;
        setTiposObrasDisponiveis(response);
      } catch (error) {
        if (!isMounted) return;
        setTiposObrasErro(parseWorkTypesError(error));
      }
    };

    loadWorkTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  const toggleTipoObra = (tipoId: number) => {
    if (tiposObrasSelecionados.includes(tipoId)) {
      setTiposObrasSelecionados(
        tiposObrasSelecionados.filter((t) => t !== tipoId),
      );
    } else {
      setTiposObrasSelecionados([
        ...tiposObrasSelecionados,
        tipoId,
      ]);
    }
  };

  const adicionarTag = () => {
    if (novaTag.trim() && !tags.includes(novaTag.trim())) {
      setTags([...tags, novaTag.trim()]);
      setNovaTag("");
    }
  };

  const removerTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleKeyDownTag = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      adicionarTag();
    }
  };

  const handleAvatarChange = (file: File | null) => {
    setLogoFile(file);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!clientSnapshot) {
      setSubmitError("Cliente inválido.");
      return;
    }

    if (!nomeCliente.trim()) {
      setSubmitError("O nome do cliente é obrigatório.");
      return;
    }

    setIsSaving(true);

    const payload: ClientPayload = {
      name: nomeCliente.trim(),
      taxId: nif.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
      website: website.trim() || null,
      address: morada.trim() || null,
      postalCode: codigoPostal.trim() || null,
      city: localidade.trim() || null,
      country: pais.trim() || null,
      primaryContactName: contactoNome.trim() || null,
      primaryContactRole: funcao.trim() || null,
      primaryContactEmail: contactoEmail.trim() || null,
      primaryContactPhone: contactoTelefone.trim() || null,
      paymentTerms: condicoesPagamento,
      tags,
      workTypeIds: tiposObrasSelecionados,
      notes: notasInternas.trim() || null,
      activeProjectsCount: clientSnapshot.activeProjectsCount,
      lastInteraction: clientSnapshot.lastInteraction ?? null,
    };

    try {
      await updateClient(clientSnapshot.id, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    if (logoFile) {
      try {
        await uploadClientLogo(clientSnapshot.id, logoFile);
      } catch {
        setSubmitError(
          "Cliente atualizado, mas não foi possível guardar o logotipo.",
        );
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    navigate("/clientes", {
      state: { toast: "Cliente atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/clientes")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-cliente-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Cliente"
        subtitle="Atualize os dados do cliente, contacto principal e informações de faturação."
        actions={actions}
        backButton={{ onClick: () => navigate("/clientes") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-cliente-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Cliente
                  </h3>

                  <div>
                    <label
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Logotipo do Cliente
                    </label>
                    <AvatarUploader
                      currentImage={logoUrl}
                      onImageChange={handleAvatarChange}
                      variant="entity"
                      label=""
                      subtitle="Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200."
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="nomeCliente"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Nome do Cliente{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nomeCliente"
                      type="text"
                      value={nomeCliente}
                      onChange={(event) =>
                        setNomeCliente(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="nif"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      NIF
                    </label>
                    <input
                      id="nif"
                      type="text"
                      value={nif}
                      onChange={(event) => setNif(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(event) =>
                        setEmail(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="telefone"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Telefone
                    </label>
                    <input
                      id="telefone"
                      type="tel"
                      value={telefone}
                      onChange={(event) =>
                        setTelefone(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="website"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Website
                    </label>
                    <input
                      id="website"
                      type="url"
                      value={website}
                      onChange={(event) =>
                        setWebsite(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="morada"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Morada
                    </label>
                    <input
                      id="morada"
                      type="text"
                      value={morada}
                      onChange={(event) =>
                        setMorada(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="codigoPostal"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Código Postal
                    </label>
                    <input
                      id="codigoPostal"
                      type="text"
                      value={codigoPostal}
                      onChange={(event) =>
                        setCodigoPostal(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="localidade"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Localidade
                    </label>
                    <input
                      id="localidade"
                      type="text"
                      value={localidade}
                      onChange={(event) =>
                        setLocalidade(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="pais"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      País
                    </label>
                    <input
                      id="pais"
                      type="text"
                      value={pais}
                      onChange={(event) =>
                        setPais(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Contacto Principal
                  </h3>

                  <div>
                    <label
                      htmlFor="contactoNome"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Nome
                    </label>
                    <input
                      id="contactoNome"
                      type="text"
                      value={contactoNome}
                      onChange={(event) =>
                        setContactoNome(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="funcao"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Função / Cargo
                    </label>
                    <input
                      id="funcao"
                      type="text"
                      value={funcao}
                      onChange={(event) =>
                        setFuncao(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contactoEmail"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Email
                    </label>
                    <input
                      id="contactoEmail"
                      type="email"
                      value={contactoEmail}
                      onChange={(event) =>
                        setContactoEmail(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contactoTelefone"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Telefone
                    </label>
                    <input
                      id="contactoTelefone"
                      type="tel"
                      value={contactoTelefone}
                      onChange={(event) =>
                        setContactoTelefone(event.target.value)
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Preferências & Tags
                  </h3>

                  <div>
                    <label
                      htmlFor="condicoesPagamento"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Condições de pagamento
                    </label>
                    <div className="relative">
                      <select
                        id="condicoesPagamento"
                        value={condicoesPagamento}
                        onChange={(e) =>
                          setCondicoesPagamento(e.target.value)
                        }
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none pr-10"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                        disabled={isLoading}
                      >
                        <option value="Pronto pagamento">
                          Pronto pagamento
                        </option>
                        <option value="15 dias">15 dias</option>
                        <option value="30 dias">30 dias</option>
                        <option value="60 dias">60 dias</option>
                        <option value="90 dias">90 dias</option>
                      </select>
                      <ChevronDown20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Tipo de obras
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {tiposObrasDisponiveis.map((tipo) => (
                        <button
                          key={tipo.id}
                          type="button"
                          onClick={() => toggleTipoObra(tipo.id)}
                          className={`px-3 py-1.5 rounded transition-colors ${
                            tiposObrasSelecionados.includes(tipo.id)
                              ? "bg-primary text-primary-foreground"
                              : "bg-secondary hover:bg-accent border border-border text-foreground"
                          }`}
                          style={{
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-caption)",
                            fontWeight:
                              tiposObrasSelecionados.includes(tipo.id)
                                ? "var(--font-weight-semi-bold)"
                                : "var(--font-weight-normal)",
                          }}
                          disabled={isLoading}
                        >
                          {tipo.name}
                        </button>
                      ))}
                    </div>
                    {tiposObrasErro && (
                      <p className="mt-2 text-sm text-destructive">
                        {tiposObrasErro}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Tags
                    </label>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {tags.map((tag) => (
                        <div
                          key={tag}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent border border-border rounded"
                          style={{
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => removerTag(tag)}
                            className="hover:text-primary transition-colors"
                            disabled={isLoading}
                          >
                            <Dismiss20Regular className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={novaTag}
                        onChange={(e) =>
                          setNovaTag(e.target.value)
                        }
                        onKeyDown={handleKeyDownTag}
                        placeholder="Adicionar nova tag"
                        className="flex-1 px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-base)",
                        }}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={adicionarTag}
                        className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                        style={{
                          borderRadius: "var(--radius-button)",
                        }}
                        disabled={isLoading}
                      >
                        <Add20Regular className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Notas Internas
                  </h3>

                  <textarea
                    id="notasInternas"
                    rows={6}
                    placeholder="Adicione notas internas sobre este cliente. Esta informação não será partilhada com o cliente."
                    value={notasInternas}
                    onChange={(event) =>
                      setNotasInternas(event.target.value)
                    }
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </form>

          {clientSnapshot && (
            <div className="mt-6">
              <EntityDocumentsSection
                ownerType="client"
                ownerId={clientSnapshot.id}
                section="contract"
                title="Contratos e Documentos"
                variant="card"
                showCounterparty
                categories={CONTRACT_DOCUMENT_CATEGORIES}
                emptyLabel="Sem contratos ou documentos arquivados."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
